/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ChangeEvent, useEffect, useState } from 'react';
import MasterTable from './MasterTable';
import FileAPI from 'api/FileAPI';
import UploadColumns, { ColumnKeys } from './UploadColumns';
import UploadRow from './UploadRow';
import { Button, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { useGetDatabaseListItemsByGroupId } from 'api/bitedb/database/database.hooks';
import { getProductsByDatabaseId } from 'api/bitedb/products/products.service';
import { GlobalContext } from 'providers/GlobalProvider';

const UploadsTable: React.FC = () => {
  const { cur_user } = React.useContext(GlobalContext);
  const [localSearchTerm, setLocalSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<ColumnKeys>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [uploads, setUploads] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [selectedDatabaseId, setSelectedDatabaseId] = useState('');
  const [selectedDatabaseProductId, setSelectedDatabaseProductId] =
    useState('');
  const [databaseProducts, setDatabaseProducts] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const itemsPerPage = 10;
  const selectedTeamId = cur_user?.selected_team?.id ?? '';

  const { allData: productDatabases } = useGetDatabaseListItemsByGroupId(
    selectedTeamId,
    'products',
  );

  const [paginationDocs, setPaginationDocs] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 5,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const fetchUploads = async () => {
    setIsLoading(true);
    const response = await FileAPI.getUploadsByGroupId(selectedTeamId, {
      page: currentPage,
      limit: itemsPerPage,
      sortBy: sortBy,
      sortOrder: sortOrder,
    });
    if (response?.success && Array.isArray(response.result.data)) {
      setUploads(response.result.data);
      setPaginationDocs(response.result.pagination);
    } else {
      setUploads([]);
      setPaginationDocs({
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 5,
        hasNextPage: false,
        hasPreviousPage: false,
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchUploads();
  }, []);

  const handlePageChange = (_e: ChangeEvent<unknown>, newPage: number) => {
    setCurrentPage(newPage);
    fetchUploads();
  };

  const handleSort = (key: ColumnKeys) => {
    const nextSortOrder =
      sortBy === key && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(key);
    setSortOrder(nextSortOrder);
    fetchUploads();
  };

  const handleUpload = async () => {
    if (!fileToUpload) {
      enqueueSnackbar('Please select a file to upload.', {
        variant: 'warning',
      });
      return;
    }
    if (!selectedDatabaseId) {
      enqueueSnackbar('Please select a database.', { variant: 'warning' });
      return;
    }
    if (!selectedDatabaseProductId) {
      enqueueSnackbar('Please select a product.', { variant: 'warning' });
      return;
    }

    setIsUploading(true);
    const uploadResponse = await FileAPI.uploadFile(
      fileToUpload,
      selectedDatabaseId,
      selectedDatabaseProductId,
    );
    if (uploadResponse?.success) {
      enqueueSnackbar('File uploaded successfully.', { variant: 'success' });
      fetchUploads();
      setIsUploadModalOpen(false);
      setFileToUpload(null);
      setSelectedDatabaseId('');
      setSelectedDatabaseProductId('');
      setDatabaseProducts([]);
    } else {
      enqueueSnackbar(uploadResponse?.result || 'Failed to upload file.', {
        variant: 'error',
      });
    }
    setIsUploading(false);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileToUpload(file);
    e.target.value = '';
  };

  const handleDatabaseChange = async (databaseId: string) => {
    setSelectedDatabaseId(databaseId);
    setSelectedDatabaseProductId('');
    if (!databaseId) {
      setDatabaseProducts([]);
      return;
    }

    const productsResponse = await getProductsByDatabaseId(databaseId, {
      page: 1,
      limit: 100,
      sortBy: 'created_at',
      sortOrder: 'desc',
    });
    setDatabaseProducts(productsResponse.data || []);
  };

  return (
    <div className='widget-item' style={{ width: '100%' }}>
      <div className='widget-search'>
        <div className='widget-search-input'>
          <input
            placeholder='Search Uploads'
            id='upload-search'
            type='text'
            className='screening-input-field-content'
            style={{ width: '680px' }}
            name='upload-search'
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
          />
        </div>
        <div
          className='btn-new-automate'
          onClick={() => setIsUploadModalOpen(true)}
        >
          <div>Upload File</div>
        </div>
      </div>
      <div className='widget-content'>
        <MasterTable
          cols={
            <UploadColumns
              sortData={handleSort}
              sortConfig={{ key: sortBy, direction: sortOrder }}
            />
          }
          suppliersData={uploads}
          selectedItems={[]}
          setSelectedItems={(items: any[]) => items}
          loading={isLoading}
          currentPage={paginationDocs.currentPage}
          totalPages={paginationDocs.totalPages}
          onPageChange={handlePageChange}
          customRowComponent={(props: any) => (
            <UploadRow
              keyVal={props.transaction.id}
              transaction={props.transaction}
            />
          )}
        />
      </div>
      <Dialog
        className='dialog_box'
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle className='modal-header-content modal-title'>
          Upload File
        </DialogTitle>
        <DialogContent className='dialog-content-base'>
          <div className='audit-add-modal-cont-body-frame-form'>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <label className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                File
              </label>
              <input type='file' onChange={handleFileChange} />
            </div>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <label className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                Database
              </label>
              <select
                className='audit-add-modal-cont-body-frame-form-row-input'
                value={selectedDatabaseId}
                onChange={(e) => handleDatabaseChange(e.target.value)}
              >
                <option value=''>Select a database</option>
                {(productDatabases || []).map((database: any) => (
                  <option key={database.id} value={database.id}>
                    {database.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='audit-add-modal-cont-body-frame-form-row'>
              <label className='audit-add-modal-cont-body-frame-form-row-input-label-text'>
                Product
              </label>
              <select
                className='audit-add-modal-cont-body-frame-form-row-input'
                value={selectedDatabaseProductId}
                onChange={(e) => setSelectedDatabaseProductId(e.target.value)}
              >
                <option value=''>Select a product</option>
                {databaseProducts.map((product: any) => (
                  <option
                    key={product.database_product_id}
                    value={product.database_product_id}
                  >
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
            <div className='audit-modal-cont-footer-content-btn-upload'>
              <Button onClick={handleUpload} disabled={isUploading}>
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UploadsTable;
